import "@testing-library/jest-dom";
import { fireEvent, render, screen } from "@testing-library/react";
import { CanvasRelatedRecordsRenderer } from "./CanvasRelatedRecordsRenderer";
import { useRelatedRecords } from "../../../hooks/useRelatedRecords";
let mockRuntime: any;
let mockGrid: any;
const mockCreate = jest.fn().mockResolvedValue({});
const mockUpdate = jest.fn().mockResolvedValue({});
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => mockRuntime,
}));
jest.mock("../../../hooks/useRelatedRecords", () => ({
  useRelatedRecords: jest.fn(),
}));
jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewQuery: () => ({ data: { id: "jobs", fields: [], joins: [] } }),
  useCreateRowMutation: () => ({ mutateAsync: mockCreate }),
  useUpdateRowMutation: () => ({ mutateAsync: mockUpdate }),
}));
jest.mock("../../viewGrid", () => ({
  ViewGridNew: (props: any) => {
    mockGrid = props;
    return (
      <div>
        Related grid
        <button onClick={() => props.onPageChange(1)}>Next page</button>
      </div>
    );
  },
}));
jest.mock("react-router-dom", () => ({
  useNavigate: () => jest.fn(),
  useLocation: () => ({ pathname: "/portal", search: "" }),
}));
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => jest.fn(),
}));
const query = useRelatedRecords as jest.Mock;
const sources = [
  {
    id: "people",
    name: "people",
    type: "view" as const,
    view_id: "employees",
    is_primary: true,
    config: {},
  },
  {
    id: "jobs",
    name: "jobs",
    type: "view" as const,
    view_id: "jobs",
    config: {},
  },
];
const element = {
  id: "related",
  type: "related_records",
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 5,
  props: {
    data_source_alias: "people",
    related_records_config: {
      related_data_source_alias: "jobs",
      relationship_path: "jobs",
      display_fields: ["title"],
      max_records: 100,
      allow_edit: true,
      allow_create: true,
    },
  },
};
beforeEach(() => {
  mockGrid = null;
  mockCreate.mockClear();
  mockUpdate.mockClear();
  mockRuntime = {
    canvasId: "canvas",
    workspaceId: "ws",
    rowId: "primary",
    primaryRowId: "primary",
    embeddingPath: [],
    interactionBus: { getSelectedRow: () => null },
  };
  query.mockReturnValue({
    data: {
      rows: [{ _rowId: "job1", title: "Analyst" }],
      columns: [],
      totalCount: 115,
      pageSize: 100,
      hasMore: true,
    },
    page: 1,
    setPage: jest.fn(),
    refetch: jest.fn(),
  });
});
test("uses the primary source row, pages beyond 100, and writes through existing related View context", async () => {
  render(
    <CanvasRelatedRecordsRenderer element={element} dataSources={sources} />,
  );
  expect(query).toHaveBeenLastCalledWith("canvas", "related", "primary");
  expect(screen.getByText(/115/)).toBeInTheDocument();
  fireEvent.click(screen.getByText("Next page"));
  expect(query.mock.results.at(-1)?.value.setPage).toHaveBeenCalledWith(2);
  await mockGrid.onRowUpdate("job1", { title: "Manager", _version: 2 });
  expect(mockUpdate).toHaveBeenCalledWith(
    expect.objectContaining({
      viewId: "jobs",
      rowId: "job1",
      data: { title: "Manager", _version: 2 },
      context: { canvasId: "canvas", elementId: "related", embeddingPath: [] },
    }),
  );
  await mockGrid.onRowCreate({ title: "New" });
  expect(mockCreate).toHaveBeenCalledWith(
    expect.objectContaining({
      viewId: "jobs",
      context: expect.objectContaining({ elementId: "related" }),
    }),
  );
});
test("uses the selected source row but rejects another alias returned by bus fallback", () => {
  mockRuntime.interactionBus.getSelectedRow = () => ({
    rowId: "selected",
    dataSourceAlias: "people",
  });
  const { rerender } = render(
    <CanvasRelatedRecordsRenderer element={element} dataSources={sources} />,
  );
  expect(query).toHaveBeenLastCalledWith("canvas", "related", "selected");
  mockRuntime.interactionBus.getSelectedRow = () => ({
    rowId: "wrong",
    dataSourceAlias: "other",
  });
  mockRuntime.rowId = "wrong";
  mockRuntime.primaryRowId = "primary";
  rerender(
    <CanvasRelatedRecordsRenderer element={element} dataSources={sources} />,
  );
  expect(query).toHaveBeenLastCalledWith("canvas", "related", "primary");
  const secondary = {
    ...element,
    props: { ...element.props, data_source_alias: "jobs" },
  };
  rerender(
    <CanvasRelatedRecordsRenderer element={secondary} dataSources={sources} />,
  );
  expect(query).toHaveBeenLastCalledWith("canvas", "related", null);
  expect(screen.getByText(/Select a record/)).toBeInTheDocument();
});
test("read-only effective element disables create and edit even when config enables both", () => {
  render(
    <CanvasRelatedRecordsRenderer
      element={{ ...element, effectiveAccess: "read" }}
      dataSources={sources}
    />,
  );
  expect(mockGrid).toMatchObject({
    allowCreate: false,
    allowEdit: false,
    onRowCreate: undefined,
    onRowUpdate: undefined,
    allowDelete: false,
    allowImport: false,
  });
});
test("config write flags are off by default and explicit query failures show no rows", () => {
  const noFlags = {
    ...element,
    props: {
      ...element.props,
      related_records_config: {
        ...element.props.related_records_config,
        allow_create: false,
        allow_edit: false,
      },
    },
  };
  const { rerender } = render(
    <CanvasRelatedRecordsRenderer element={noFlags} dataSources={sources} />,
  );
  expect(mockGrid).toMatchObject({ allowCreate: false, allowEdit: false });
  query.mockReturnValue({ page: 1, error: new Error("Access denied") });
  rerender(
    <CanvasRelatedRecordsRenderer element={element} dataSources={sources} />,
  );
  expect(screen.getByText("Access denied")).toBeInTheDocument();
  expect(screen.queryByText("Related grid")).not.toBeInTheDocument();
});
